/**
 * La géométrie du glisser de « À faire » : quelle ligne est sous le doigt, et où elle tombera.
 *
 * Pur, sans geste ni rendu : `<ActivitiesSection>` mesure ses lignes au repos et pose ici le doigt.
 * Les hauteurs sont mesurées, pas supposées — un titre long passe à deux lignes selon la largeur, et
 * une hauteur en dur ferait sauter la cible d'un cran (la leçon de `StageDragList.js`).
 *
 * Les positions sont celles du repos : pendant le glisser, la ligne quitte sa place et son
 * emplacement d'arrivée est plus haut qu'elle, mais le doigt, lui, se lit sur la liste d'avant.
 */

/** Le filet entre deux lignes de la carte-liste (`<CardList>`, `borderTopWidth: 1`). */
export const ROW_DIVIDER = 1

/** L'index de la ligne sous `y` (depuis le haut de la carte), ou `null` hors des lignes. */
export function rowIndexAt(y: number, heights: readonly number[], divider = ROW_DIVIDER): number | null {
    let top = 0
    for (let index = 0; index < heights.length; index += 1) {
        const height = heights[index] ?? 0
        if (y >= top && y < top + height + divider) return index
        top += height + divider
    }
    return null
}

/**
 * La place où tombe la ligne `from` quand le doigt s'est déplacé de `offset` (positif vers le bas) :
 * elle passe une voisine dès que son milieu dépasse celui de la voisine. L'index est celui de la
 * liste une fois la ligne retirée de sa place — le `toIndex` d'`ActivitiesReorder`.
 */
export function reorderTarget(from: number, offset: number, heights: readonly number[], divider = ROW_DIVIDER): number {
    let target = from
    let travelled = 0
    if (offset > 0) {
        for (let index = from + 1; index < heights.length; index += 1) {
            const height = (heights[index] ?? 0) + divider
            travelled += height
            if (offset > travelled - height / 2) target = index
            else break
        }
    } else if (offset < 0) {
        for (let index = from - 1; index >= 0; index -= 1) {
            const height = (heights[index] ?? 0) + divider
            travelled += height
            if (-offset > travelled - height / 2) target = index
            else break
        }
    }
    return target
}

/** La liste une fois l'élément `from` posé à `to` (retiré, puis inséré). Neuve, même quand rien ne bouge. */
export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
    const next = items.slice()
    if (from < 0 || from >= next.length) return next
    const [moved] = next.splice(from, 1)
    next.splice(Math.min(Math.max(to, 0), next.length), 0, moved as T)
    return next
}
